import { useEffect, useState, useRef, useCallback } from 'react';
import { majorsApi, type MajorCompareResult, type MajorCompareSchool, type HotMajor } from '../api';
import { Search, X, TrendingUp, TrendingDown, Minus, GraduationCap, Flame, ChevronUp, ChevronDown, ArrowUpDown, Sparkles } from 'lucide-react';
import MajorKnowledgeModal from '../components/MajorKnowledgeModal';

// 防抖 hook
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface SuggestionItem {
  name: string;
  school_count?: number;
  record_count?: number;
}

type SortKey = 'school_name' | 'city' | string; // string 年份如 "2024"
type SortDir = 'asc' | 'desc';

export default function MajorExplorer() {
  // 搜索状态
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);
  const [suggestLoading, setSuggestLoading] = useState(false);

  // 热门专业
  const [hotMajors, setHotMajors] = useState<HotMajor[]>([]);

  // 选中专业 & 对比数据
  const [selectedMajor, setSelectedMajor] = useState<string | null>(null);
  const [compareData, setCompareData] = useState<MajorCompareResult | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // 筛选 & 排序
  const [yearFilter, setYearFilter] = useState<string | 'all'>('all');
  const [schoolTypeFilter, setSchoolTypeFilter] = useState<'all' | '985' | '211'>('all');
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; dir: SortDir }>({ key: '2024', dir: 'asc' });

  // 知识库弹窗（全屏专业详解）
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [knowledgeMajor, setKnowledgeMajor] = useState<string>('');

  const searchRef = useRef<HTMLInputElement>(null);
  const suggestRef = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(searchInput, 280);

  // 初始化热门专业
  useEffect(() => {
    majorsApi.hot(12).then(res => {
      setHotMajors(res.data.data || []);
    }).catch(console.error);
  }, []);

  // 搜索建议（防抖）
  useEffect(() => {
    if (!debouncedSearch.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setSuggestLoading(true);
    majorsApi.names(debouncedSearch.trim()).then(res => {
      const data = res.data.data || [];
      setSuggestions(data);
      setShowSuggestions(data.length > 0);
      setSuggestionIndex(-1);
    }).catch(console.error).finally(() => setSuggestLoading(false));
  }, [debouncedSearch]);

  // 点击外部关闭下拉
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target as Node) &&
          searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 选择专业，加载对比数据
  const selectMajor = useCallback(async (name: string) => {
    setSelectedMajor(name);
    setSearchInput(name);
    setShowSuggestions(false);
    setCompareLoading(true);
    setCompareData(null);
    try {
      const res = await majorsApi.compare(name);
      const data: MajorCompareResult = res.data.data;
      setCompareData(data);
      // 默认按最新年份排序
      if (data.years && data.years.length > 0) {
        setSortConfig({ key: String(data.years[0]), dir: 'asc' });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCompareLoading(false);
    }
  }, []);

  // 键盘导航
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSuggestionIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSuggestionIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestionIndex >= 0 && suggestions[suggestionIndex]) {
        selectMajor(suggestions[suggestionIndex].name);
      } else if (searchInput.trim()) {
        selectMajor(searchInput.trim());
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSelectedMajor(null);
    setCompareData(null);
    setSuggestions([]);
    setShowSuggestions(false);
    searchRef.current?.focus();
  };

  // 打开知识库弹窗（全屏专业详解）
  const openKnowledge = useCallback((majorName: string) => {
    setKnowledgeMajor(majorName);
    setKnowledgeOpen(true);
  }, []);

  // 排序处理
  const handleSort = (key: SortKey) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: key === 'school_name' || key === 'city' ? 'asc' : 'asc' };
    });
  };

  // 过滤 + 排序后的院校列表
  const filteredSchools = (() => {
    if (!compareData) return [];
    let list = [...compareData.schools];

    // 院校类型筛选
    if (schoolTypeFilter === '985') list = list.filter(s => s.is985 === 1);
    else if (schoolTypeFilter === '211') list = list.filter(s => s.is985 === 1 || s.is211 === 1);

    // 排序
    list.sort((a, b) => {
      const { key, dir } = sortConfig;
      const mult = dir === 'asc' ? 1 : -1;

      if (key === 'school_name') {
        return mult * a.school_name.localeCompare(b.school_name, 'zh-CN');
      }
      if (key === 'city') {
        return mult * (a.city || '').localeCompare(b.city || '', 'zh-CN');
      }
      // 按年份位次排序（key 为年份字符串如 "2024"）
      const rankA = a.scores[key]?.lowest_rank ?? 999999;
      const rankB = b.scores[key]?.lowest_rank ?? 999999;
      return mult * (rankA - rankB);
    });

    return list;
  })();

  // 显示的年份列（根据筛选）
  const displayYears = compareData
    ? (yearFilter === 'all' ? compareData.years : compareData.years.filter(y => String(y) === String(yearFilter)))
    : [];

  // 趋势图标
  const TrendIcon = ({ school }: { school: MajorCompareSchool }) => {
    if (!compareData || compareData.years.length < 2) return null;
    const y0 = String(compareData.years[0]); // 最新年
    const y1 = String(compareData.years[1]); // 上一年
    const s0 = school.scores[y0]?.lowest_score;
    const s1 = school.scores[y1]?.lowest_score;
    if (!s0 || !s1) return <span className="text-gray-300">-</span>;
    const diff = s0 - s1;
    if (diff > 0) return <span className="inline-flex items-center gap-0.5 text-red-500 font-medium text-xs"><TrendingUp size={12} />+{diff}</span>;
    if (diff < 0) return <span className="inline-flex items-center gap-0.5 text-green-600 font-medium text-xs"><TrendingDown size={12} />{diff}</span>;
    return <span className="inline-flex items-center gap-0.5 text-gray-400 text-xs"><Minus size={12} />持平</span>;
  };

  const SortIcon = ({ colKey }: { colKey: SortKey }) => {
    if (sortConfig.key !== colKey) return <ArrowUpDown size={12} className="text-gray-300 ml-1" />;
    return sortConfig.dir === 'asc'
      ? <ChevronUp size={12} className="text-blue-500 ml-1" />
      : <ChevronDown size={12} className="text-blue-500 ml-1" />;
  };

  return (
    <div className="p-3 md:p-6 max-w-7xl mx-auto">
      {/* 顶部标题 */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">专业探索</h1>
        <p className="text-gray-500 text-sm mt-1">
          搜索某个专业，查看各院校录取分数横向对比
        </p>
      </div>

      {/* 搜索框 */}
      <div className="relative mb-6">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            placeholder="输入专业名称，如：计算机科学与技术、法学、人工智能..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            className="w-full pl-11 pr-12 py-3.5 text-base border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-blue-500 transition-colors shadow-sm bg-white"
          />
          {searchInput && (
            <button
              onClick={clearSearch}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={16} className="text-gray-400" />
            </button>
          )}
        </div>

        {/* 搜索建议下拉 */}
        {showSuggestions && (
          <div
            ref={suggestRef}
            className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden"
          >
            {suggestLoading ? (
              <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                搜索中...
              </div>
            ) : (
              <ul>
                {suggestions.map((s, i) => (
                  <li
                    key={s.name}
                    onMouseDown={() => selectMajor(s.name)}
                    onMouseEnter={() => setSuggestionIndex(i)}
                    className={`px-4 py-3 cursor-pointer flex items-center justify-between text-sm transition-colors ${
                      i === suggestionIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium text-gray-800">{s.name}</span>
                    <span className="text-gray-400 text-xs ml-4 shrink-0">
                      {s.school_count !== undefined
                        ? `${s.school_count} 所院校`
                        : s.record_count ? `${s.record_count} 条数据` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* 未选专业：热门专业标签 */}
      {!selectedMajor && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Flame size={16} className="text-orange-500" />
            <span className="text-sm font-medium text-gray-600">热门专业</span>
            <span className="text-xs text-gray-400">（按开设院校数排序）</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-8">
            {hotMajors.map(m => (
              <button
                key={m.major_name}
                onClick={() => selectMajor(m.major_name)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm"
              >
                <span>{m.major_name}</span>
                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-md">
                  {m.school_count}所
                </span>
              </button>
            ))}
          </div>

          {/* 引导文案 */}
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <GraduationCap size={52} className="opacity-20 mb-4" />
            <p className="text-base font-medium opacity-60">搜索或点击热门专业</p>
            <p className="text-sm opacity-40 mt-1">查看该专业在各院校的录取分数横向对比</p>
          </div>
        </div>
      )}

      {/* 已选专业：对比视图 */}
      {selectedMajor && (
        <div>
          {/* 专业标题 + 筛选栏 */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-gray-800">{selectedMajor}</h2>
              {compareData && !compareLoading && (
                <span className="text-sm text-gray-500 bg-gray-100 px-2.5 py-1 rounded-lg">
                  {filteredSchools.length} 所院校
                  {compareData.total_schools !== filteredSchools.length && ` / 共 ${compareData.total_schools} 所`}
                </span>
              )}
              {/* 醒目的「专业详解」按钮 */}
              <button
                onClick={() => openKnowledge(selectedMajor || '')}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-sm font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-600 transition-all transform hover:-translate-y-0.5"
              >
                <Sparkles size={16} />
                专业详解
              </button>
            </div>
            <div className="flex items-center gap-2">
              {/* 年份筛选 */}
              {compareData && compareData.years.length > 1 && (
                <div className="flex bg-gray-100 rounded-xl p-1">
                  <button
                    onClick={() => setYearFilter('all')}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-colors font-medium ${
                      yearFilter === 'all' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    全部年份
                  </button>
                  {compareData.years.map(y => (
                    <button
                      key={y}
                      onClick={() => setYearFilter(String(y))}
                      className={`px-3 py-1.5 text-xs rounded-lg transition-colors font-medium ${
                        yearFilter === String(y) ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              )}
              {/* 院校类型筛选 */}
              <div className="flex bg-gray-100 rounded-xl p-1">
                {[
                  { key: 'all', label: '全部' },
                  { key: '985', label: '985' },
                  { key: '211', label: '211+' },
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={() => setSchoolTypeFilter(item.key as typeof schoolTypeFilter)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-colors font-medium ${
                      schoolTypeFilter === item.key ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 对比表格 */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {compareLoading ? (
              <div className="p-8">
                <div className="space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="h-5 bg-gray-200 rounded w-1/4 animate-pulse" />
                      <div className="h-5 bg-gray-200 rounded w-1/6 animate-pulse" />
                      <div className="h-5 bg-gray-200 rounded w-1/5 animate-pulse" />
                      <div className="h-5 bg-gray-200 rounded w-1/6 animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            ) : filteredSchools.length === 0 ? (
              <div className="py-16 text-center text-gray-400">
                <GraduationCap size={40} className="opacity-30 mx-auto mb-3" />
                <p>该专业暂无录取数据</p>
                <p className="text-xs mt-1 opacity-60">可尝试其他专业或院校类型</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      {/* 固定列 */}
                      <th
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('school_name')}
                      >
                        <span className="inline-flex items-center">
                          院校 <SortIcon colKey="school_name" />
                        </span>
                      </th>
                      <th
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('city')}
                      >
                        <span className="inline-flex items-center">
                          城市 <SortIcon colKey="city" />
                        </span>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        类型
                      </th>
                      {/* 动态年份列 */}
                      {displayYears.map(year => (
                        <th
                          key={year}
                          colSpan={2}
                          className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200 cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort(String(year))}
                        >
                          <span className="inline-flex items-center justify-center">
                            {year} 年 <SortIcon colKey={String(year)} />
                          </span>
                        </th>
                      ))}
                      {/* 趋势列（仅全部年份且有≥2年数据时显示）*/}
                      {yearFilter === 'all' && compareData && compareData.years.length >= 2 && (
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                          趋势
                        </th>
                      )}
                    </tr>
                    {/* 子表头：最低分 / 位次 */}
                    <tr className="bg-gray-50/60 border-b border-gray-100">
                      <th colSpan={3} />
                      {displayYears.map(year => (
                        <>
                          <th key={`${year}-score`} className="px-3 py-1.5 text-center text-xs font-normal text-gray-400 border-l border-gray-200 w-20">
                            最低分
                          </th>
                          <th key={`${year}-rank`} className="px-3 py-1.5 text-center text-xs font-normal text-gray-400 w-24">
                            位次
                          </th>
                        </>
                      ))}
                      {yearFilter === 'all' && compareData && compareData.years.length >= 2 && (
                        <th className="border-l border-gray-200" />
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredSchools.map((school, idx) => {
                      const is985 = school.is985 === 1;
                      const is211 = school.is211 === 1;
                      const rowBg = is985
                        ? 'bg-red-50/30 hover:bg-red-50/60'
                        : is211
                        ? 'bg-blue-50/20 hover:bg-blue-50/50'
                        : 'hover:bg-gray-50/80';

                      return (
                        <tr key={idx} className={`transition-colors ${rowBg}`}>
                          <td className="px-4 py-3">
                            <span className="font-medium text-gray-800">{school.school_name}</span>
                          </td>
                          <td className="px-3 py-3 text-gray-500 text-xs whitespace-nowrap">
                            {school.city || school.school_province || '-'}
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex gap-1">
                              {is985 ? (
                                <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">985</span>
                              ) : is211 ? (
                                <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium">211</span>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </div>
                          </td>
                          {displayYears.map(year => {
                            const sc = school.scores[String(year)];
                            return (
                              <>
                                <td key={`${year}-score`} className="px-3 py-3 text-center border-l border-gray-100">
                                  {sc?.lowest_score ? (
                                    <span className="font-semibold text-gray-800">{sc.lowest_score}</span>
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                                <td key={`${year}-rank`} className="px-3 py-3 text-center text-gray-500 text-xs">
                                  {sc?.lowest_rank ? sc.lowest_rank.toLocaleString() : '-'}
                                </td>
                              </>
                            );
                          })}
                          {yearFilter === 'all' && compareData && compareData.years.length >= 2 && (
                            <td className="px-3 py-3 text-center border-l border-gray-100">
                              <TrendIcon school={school} />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 底部说明 */}
          {!compareLoading && compareData && compareData.years.length > 0 && (
            <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-red-50 border border-red-200 rounded inline-block" />
                985院校
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 bg-blue-50 border border-blue-200 rounded inline-block" />
                211院校
              </span>
              <span>· 位次越小，录取难度越高 · 数据来源：福建省招生考试院</span>
            </div>
          )}
        </div>
      )}

      {/* 全屏专业详解弹窗 */}
      <MajorKnowledgeModal
        isOpen={knowledgeOpen}
        onClose={() => setKnowledgeOpen(false)}
        majorName={knowledgeMajor}
      />
    </div>
  );
}
